import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload,
  FileSpreadsheet,
  Download,
  Trash2,
  RefreshCw,
  ArrowRight,
  Search,
  ChevronLeft,
  ChevronRight,
  GitCompare,
  MapPinned,
  Pencil,
  History,
} from "lucide-react";
import { RealizadoCorrigirLinhaDialog } from "@/components/realizado-financeiro/RealizadoCorrigirLinhaDialog";
import { RealizadoHistoricoAlteracoes } from "@/components/realizado-financeiro/RealizadoHistoricoAlteracoes";
import { useSupabase } from "@/hooks/useSupabase";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { auditTripletSets, keyExact, rowToTriplet, type SpellingDivergence, type TripletCount } from "@/lib/realizadoOrcamentoTripletAudit";
import {
  auditAreaPairSets,
  areaPairKeyExact,
  rowToAreaPair,
  type AreaPairCount,
  type AreaSpellingDivergence,
} from "@/lib/realizadoOrcamentoAreaAudit";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";
import { parseExcelDate as parseDate, parseExcelNumber as parseNumber } from "@/lib/excelImportParsers";

const TABLE = "solis_realizado_financeiro_complementar" as const;

/** Áreas em que o orçamento usa par homónimo (área = subárea tratada), não '-'. */
const AREAS_SUB_HOMONIMA_ORCAMENTO = new Set([
  "recursos humanos",
  "conselho gestor",
  "originação e estruturação",
  "originacao e estruturacao",
]);

function areaUsaSubHomonimaOrcamento(areaTratado: string): boolean {
  return AREAS_SUB_HOMONIMA_ORCAMENTO.has(areaTratado.trim().toLowerCase());
}

/** PostgREST limita ~1000 linhas por request; buscamos em laço até esgotar. */
const FETCH_RANGE_SIZE = 1000;
const AUDIT_PAGE_SIZE = 25;

interface OrcamentoTripletRow {
  tipo_receita_despesa: string | null;
  classificacao_receita_despesa: string | null;
  descricao_receita_despesa: string | null;
  valor: number | null;
  area: string | null;
  sub_area: string | null;
}

type TripletCountValorOrc = TripletCount & { sumValorOrcamento: number };
type TripletCountValorRea = TripletCount & { sumValorRealizado: number };
type AreaPairCountValorOrc = AreaPairCount & { sumValorOrcamento: number };
type AreaPairCountValorRea = AreaPairCount & { sumValorRealizado: number };

function auditRowMatchesQuery(row: TripletCount, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const hay = `${row.tipo} ${row.classif} ${row.descr}`.toLowerCase();
  const tokens = n.split(/\s+/).filter(Boolean);
  return tokens.every((t) => hay.includes(t));
}

function spellingMatchesQuery(d: SpellingDivergence, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const hay = `${d.normalizedKey} ${d.orcamentoVariants.map((v) => `${v.tipo} ${v.classif} ${v.descr}`).join(" ")} ${d.realizadoVariants.map((v) => `${v.tipo} ${v.classif} ${v.descr}`).join(" ")}`.toLowerCase();
  const tokens = n.split(/\s+/).filter(Boolean);
  return tokens.every((t) => hay.includes(t));
}

function areaAuditRowMatchesQuery(row: AreaPairCount, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const hay = `${row.area} ${row.subArea}`.toLowerCase();
  const tokens = n.split(/\s+/).filter(Boolean);
  return tokens.every((t) => hay.includes(t));
}

function areaSpellingMatchesQuery(d: AreaSpellingDivergence, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const hay = `${d.normalizedKey} ${d.orcamentoVariants.map((v) => `${v.area} ${v.subArea}`).join(" ")} ${d.realizadoVariants.map((v) => `${v.area} ${v.subArea}`).join(" ")}`.toLowerCase();
  const tokens = n.split(/\s+/).filter(Boolean);
  return tokens.every((t) => hay.includes(t));
}

type RealizadoColumnKey =
  | "conta"
  | "data"
  | "numero_documento"
  | "nome"
  | "debito"
  | "credito"
  | "saldo"
  | "cadastro_federal_cliente"
  | "cadastro_federal_fornecedor"
  | "memorando"
  | "conta_linha_numero"
  | "subsidiaria_nome"
  | "departamento"
  | "area_tratado"
  | "classe"
  | "sub_area_tratado"
  | "valor"
  | "tipo_receita_despesa"
  | "classificacao_receita_despesa"
  | "descricao_receita_despesa"
  | "tipo";

interface RealizadoItem {
  id: string;
  importacao_id: string;
  nome_arquivo: string | null;
  conta: string | null;
  tipo: string | null;
  data: string | null;
  numero_documento: string | null;
  nome: string | null;
  debito: number | null;
  credito: number | null;
  saldo: number | null;
  cadastro_federal_cliente: string | null;
  cadastro_federal_fornecedor: string | null;
  memorando: string | null;
  conta_linha_numero: string | null;
  subsidiaria_nome: string | null;
  departamento: string | null;
  area_tratado: string | null;
  classe: string | null;
  sub_area_tratado: string | null;
  valor: number | null;
  tipo_receita_despesa: string | null;
  classificacao_receita_despesa: string | null;
  descricao_receita_despesa: string | null;
  created_at: string;
}

function normalizeLabel(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Colunas na mesma ordem da aba oficial "Base para Realizado 2026" (A–U).
 * Rótulos alinhados ao Excel para mapeamento visual e auto-map canônico.
 */
const DB_COLUMNS: { key: RealizadoColumnKey; label: string; required: boolean; aliases?: string[] }[] = [
  { key: "conta", label: "Conta", required: false },
  { key: "tipo", label: "Tipo", required: false },
  { key: "data", label: "Data", required: true },
  { key: "numero_documento", label: "Número do documento", required: false },
  { key: "nome", label: "Nome", required: false },
  { key: "debito", label: "Débito", required: false, aliases: ["debito"] },
  { key: "credito", label: "Crédito", required: false, aliases: ["credito"] },
  { key: "saldo", label: "Saldo", required: false },
  { key: "cadastro_federal_cliente", label: "Cadastro federal do cliente", required: false },
  { key: "cadastro_federal_fornecedor", label: "Cadastro federal do fornecedor", required: false },
  { key: "memorando", label: "Memorando", required: false },
  { key: "conta_linha_numero", label: "Conta (linha): Número", required: false },
  { key: "subsidiaria_nome", label: "Subsidiária: Nome", required: false },
  { key: "departamento", label: "Departamento", required: false },
  { key: "area_tratado", label: "AREA tratado", required: false },
  { key: "classe", label: "Classe", required: false },
  { key: "sub_area_tratado", label: "SUB AREA tratado", required: false, aliases: ["sub area tratado"] },
  { key: "valor", label: "VALOR", required: false, aliases: ["valor"] },
  { key: "tipo_receita_despesa", label: "Tipo_Receita/Despesa", required: false },
  { key: "classificacao_receita_despesa", label: "Classificação_Receita/Despesa", required: false },
  { key: "descricao_receita_despesa", label: "Descrição_Receita/Despesa", required: false },
];

/** Cabeçalhos exatos da planilha (normalizados) → campo SQL. Evita confundir "Tipo" com "Tipo_Receita/Despesa". */
const CANONICAL_HEADER_MAP: Record<string, RealizadoColumnKey> = (() => {
  const labels = [
    "Conta",
    "Tipo",
    "Data",
    "Número do documento",
    "Nome",
    "Débito",
    "Crédito",
    "Saldo",
    "Cadastro federal do cliente",
    "Cadastro federal do fornecedor",
    "Memorando",
    "Conta (linha): Número",
    "Subsidiária: Nome",
    "Departamento",
    "AREA tratado",
    "Classe",
    "SUB AREA tratado",
    "VALOR",
    "Tipo_Receita/Despesa",
    "Classificação_Receita/Despesa",
    "Descrição_Receita/Despesa",
  ] as const;
  const keys: RealizadoColumnKey[] = [
    "conta",
    "tipo",
    "data",
    "numero_documento",
    "nome",
    "debito",
    "credito",
    "saldo",
    "cadastro_federal_cliente",
    "cadastro_federal_fornecedor",
    "memorando",
    "conta_linha_numero",
    "subsidiaria_nome",
    "departamento",
    "area_tratado",
    "classe",
    "sub_area_tratado",
    "valor",
    "tipo_receita_despesa",
    "classificacao_receita_despesa",
    "descricao_receita_despesa",
  ];
  const m: Record<string, RealizadoColumnKey> = {};
  labels.forEach((lab, i) => {
    m[normalizeLabel(lab)] = keys[i];
  });
  return m;
})();

export default function RealizadoFinanceiroPage() {
  const { supabase } = useSupabase();
  const [items, setItems] = useState<RealizadoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [excelData, setExcelData] = useState<any[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, RealizadoColumnKey>>({});
  const importMetaRef = React.useRef<{ id: string; nomeArquivo: string } | null>(null);

  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [orcamentoTripletRows, setOrcamentoTripletRows] = useState<OrcamentoTripletRow[]>([]);
  const [loadingOrcamentoAudit, setLoadingOrcamentoAudit] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditActiveTab, setAuditActiveTab] = useState("only-orc");
  const [auditPageOrc, setAuditPageOrc] = useState(1);
  const [auditPageRea, setAuditPageRea] = useState(1);
  const [auditPageFuzzy, setAuditPageFuzzy] = useState(1);
  const [areaAuditSearch, setAreaAuditSearch] = useState("");
  const [areaAuditTab, setAreaAuditTab] = useState("a-orc");
  const [areaPageOrc, setAreaPageOrc] = useState(1);
  const [areaPageRea, setAreaPageRea] = useState(1);
  const [areaPageFuzzy, setAreaPageFuzzy] = useState(1);
  const [corrigirOpen, setCorrigirOpen] = useState(false);
  const [itemParaCorrigir, setItemParaCorrigir] = useState<RealizadoItem | null>(null);
  const [historicoRegistroId, setHistoricoRegistroId] = useState<string | null>(null);
  const [historicoRefresh, setHistoricoRefresh] = useState(0);
  const selectedYearRef = React.useRef(selectedYear);
  selectedYearRef.current = selectedYear;

  const fetchYears = useCallback(async () => {
    try {
      const { data, error } = await supabase.from(TABLE).select("data");
      if (error) throw error;

      const uniqueYears = Array.from(
        new Set(
          (data || [])
            .map((item) => (item.data ? String(item.data).slice(0, 4) : null))
            .filter(Boolean) as string[]
        )
      ).sort().reverse();

      setYears(uniqueYears);
      if (uniqueYears.length > 0 && !selectedYearRef.current) {
        setSelectedYear(uniqueYears[0]);
      }
    } catch (error) {
      console.error("Erro ao buscar anos:", error);
      toast.error("Erro ao carregar anos. Verifique se a migração 081 foi aplicada.");
    }
  }, [supabase]);

  const fetchItems = useCallback(
    async (year: string) => {
      setLoading(true);
      try {
        const accumulated: RealizadoItem[] = [];
        let from = 0;
        for (;;) {
          const to = from + FETCH_RANGE_SIZE - 1;
          const { data, error } = await supabase
            .from(TABLE)
            .select("*")
            .gte("data", `${year}-01-01`)
            .lte("data", `${year}-12-31`)
            .order("data", { ascending: false })
            .range(from, to);
          if (error) throw error;
          const batch = (data || []) as RealizadoItem[];
          accumulated.push(...batch);
          if (batch.length < FETCH_RANGE_SIZE) break;
          from += FETCH_RANGE_SIZE;
        }
        setItems(accumulated);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        toast.error("Erro ao carregar dados.");
      } finally {
        setLoading(false);
      }
    },
    [supabase]
  );

  const fetchOrcamentoTripletsForYear = useCallback(
    async (year: string) => {
      setLoadingOrcamentoAudit(true);
      try {
        const accumulated: OrcamentoTripletRow[] = [];
        let from = 0;
        for (;;) {
          const to = from + FETCH_RANGE_SIZE - 1;
          const { data, error } = await supabase
            .from("solis_orcamento")
            .select("tipo_receita_despesa, classificacao_receita_despesa, descricao_receita_despesa, valor, area, sub_area")
            .ilike("mes", `${year}-%`)
            .range(from, to);
          if (error) throw error;
          const batch = (data || []) as OrcamentoTripletRow[];
          accumulated.push(...batch);
          if (batch.length < FETCH_RANGE_SIZE) break;
          from += FETCH_RANGE_SIZE;
        }
        setOrcamentoTripletRows(accumulated);
      } catch (error) {
        console.error("Erro ao carregar orçamento para auditoria:", error);
        toast.error("Auditoria: não foi possível carregar solis_orcamento.");
        setOrcamentoTripletRows([]);
      } finally {
        setLoadingOrcamentoAudit(false);
      }
    },
    [supabase]
  );

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  useEffect(() => {
    if (selectedYear) fetchItems(selectedYear);
    else setItems([]);
  }, [selectedYear, fetchItems]);

  useEffect(() => {
    if (selectedYear) void fetchOrcamentoTripletsForYear(selectedYear);
    else setOrcamentoTripletRows([]);
  }, [selectedYear, fetchOrcamentoTripletsForYear]);

  useEffect(() => {
    setTablePage(1);
  }, [searchQuery, pageSize, selectedYear]);

  useEffect(() => {
    setAuditSearch("");
    setAuditActiveTab("only-orc");
    setAuditPageOrc(1);
    setAuditPageRea(1);
    setAuditPageFuzzy(1);
    setAreaAuditSearch("");
    setAreaAuditTab("a-orc");
    setAreaPageOrc(1);
    setAreaPageRea(1);
    setAreaPageFuzzy(1);
  }, [selectedYear]);

  useEffect(() => {
    setAuditPageOrc(1);
    setAuditPageRea(1);
    setAuditPageFuzzy(1);
  }, [auditSearch]);

  useEffect(() => {
    setAreaPageOrc(1);
    setAreaPageRea(1);
    setAreaPageFuzzy(1);
  }, [areaAuditSearch]);

  const rowMatchesSearch = useCallback((item: RealizadoItem, q: string) => {
    const n = q.trim().toLowerCase();
    if (!n) return true;
    const haystack = [
      item.conta,
      item.tipo,
      item.nome,
      item.numero_documento,
      item.memorando,
      item.departamento,
      item.area_tratado,
      item.sub_area_tratado,
      item.classe,
      item.tipo_receita_despesa,
      item.classificacao_receita_despesa,
      item.descricao_receita_despesa,
      item.cadastro_federal_cliente,
      item.cadastro_federal_fornecedor,
      item.conta_linha_numero,
      item.subsidiaria_nome,
      item.data,
      item.debito != null ? String(item.debito) : "",
      item.credito != null ? String(item.credito) : "",
      item.valor != null ? String(item.valor) : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const tokens = n.split(/\s+/).filter(Boolean);
    return tokens.every((t) => haystack.includes(t));
  }, []);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    return items.filter((item) => rowMatchesSearch(item, searchQuery));
  }, [items, searchQuery, rowMatchesSearch]);

  const totalTablePages = Math.max(1, Math.ceil(filteredItems.length / pageSize));

  useEffect(() => {
    setTablePage((p) => Math.min(Math.max(1, p), totalTablePages));
  }, [totalTablePages]);

  const paginatedItems = useMemo(() => {
    const page = Math.min(tablePage, totalTablePages);
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, tablePage, pageSize, totalTablePages]);

  useRefetchOnFocus(
    useCallback(() => {
      if (selectedYearRef.current) {
        void fetchItems(selectedYearRef.current);
        void fetchOrcamentoTripletsForYear(selectedYearRef.current);
      } else void fetchYears();
    }, [fetchItems, fetchYears, fetchOrcamentoTripletsForYear])
  );

  const tripletAudit = useMemo(() => {
    const orc = orcamentoTripletRows.map((r) =>
      rowToTriplet(r.tipo_receita_despesa, r.classificacao_receita_despesa, r.descricao_receita_despesa)
    );
    const rea = items.map((r) =>
      rowToTriplet(r.tipo_receita_despesa, r.classificacao_receita_despesa, r.descricao_receita_despesa)
    );
    return auditTripletSets(orc, rea);
  }, [orcamentoTripletRows, items]);

  const valorSumByTripletKeyOrc = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of orcamentoTripletRows) {
      const p = rowToTriplet(r.tipo_receita_despesa, r.classificacao_receita_despesa, r.descricao_receita_despesa);
      if (!p.tipo && !p.classif && !p.descr) continue;
      const k = keyExact(p);
      const add = r.valor != null && !Number.isNaN(Number(r.valor)) ? Number(r.valor) : 0;
      m.set(k, (m.get(k) ?? 0) + add);
    }
    return m;
  }, [orcamentoTripletRows]);

  const valorSumByTripletKeyRea = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of items) {
      const p = rowToTriplet(r.tipo_receita_despesa, r.classificacao_receita_despesa, r.descricao_receita_despesa);
      if (!p.tipo && !p.classif && !p.descr) continue;
      const k = keyExact(p);
      const add = r.valor != null && !Number.isNaN(Number(r.valor)) ? Number(r.valor) : 0;
      m.set(k, (m.get(k) ?? 0) + add);
    }
    return m;
  }, [items]);

  const onlyOrcamentoEnriched: TripletCountValorOrc[] = useMemo(
    () =>
      tripletAudit.onlyOrcamento.map((row) => ({
        ...row,
        sumValorOrcamento: valorSumByTripletKeyOrc.get(keyExact({ tipo: row.tipo, classif: row.classif, descr: row.descr })) ?? 0,
      })),
    [tripletAudit.onlyOrcamento, valorSumByTripletKeyOrc]
  );

  const onlyRealizadoEnriched: TripletCountValorRea[] = useMemo(
    () =>
      tripletAudit.onlyRealizado.map((row) => ({
        ...row,
        sumValorRealizado: valorSumByTripletKeyRea.get(keyExact({ tipo: row.tipo, classif: row.classif, descr: row.descr })) ?? 0,
      })),
    [tripletAudit.onlyRealizado, valorSumByTripletKeyRea]
  );

  const filteredAuditOnlyOrc = useMemo(
    () => onlyOrcamentoEnriched.filter((row) => auditRowMatchesQuery(row, auditSearch)),
    [onlyOrcamentoEnriched, auditSearch]
  );
  const filteredAuditOnlyRea = useMemo(
    () => onlyRealizadoEnriched.filter((row) => auditRowMatchesQuery(row, auditSearch)),
    [onlyRealizadoEnriched, auditSearch]
  );
  const filteredAuditFuzzy = useMemo(
    () => tripletAudit.spellingDivergences.filter((d) => spellingMatchesQuery(d, auditSearch)),
    [tripletAudit.spellingDivergences, auditSearch]
  );

  const auditTotalPagesOrc = Math.max(1, Math.ceil(filteredAuditOnlyOrc.length / AUDIT_PAGE_SIZE));
  const auditTotalPagesRea = Math.max(1, Math.ceil(filteredAuditOnlyRea.length / AUDIT_PAGE_SIZE));
  const auditTotalPagesFuzzy = Math.max(1, Math.ceil(filteredAuditFuzzy.length / AUDIT_PAGE_SIZE));

  useEffect(() => {
    setAuditPageOrc((p) => Math.min(Math.max(1, p), auditTotalPagesOrc));
  }, [auditTotalPagesOrc]);
  useEffect(() => {
    setAuditPageRea((p) => Math.min(Math.max(1, p), auditTotalPagesRea));
  }, [auditTotalPagesRea]);
  useEffect(() => {
    setAuditPageFuzzy((p) => Math.min(Math.max(1, p), auditTotalPagesFuzzy));
  }, [auditTotalPagesFuzzy]);

  const sliceAuditOrc = useMemo(() => {
    const page = Math.min(auditPageOrc, auditTotalPagesOrc);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAuditOnlyOrc.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAuditOnlyOrc, auditPageOrc, auditTotalPagesOrc]);

  const sliceAuditRea = useMemo(() => {
    const page = Math.min(auditPageRea, auditTotalPagesRea);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAuditOnlyRea.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAuditOnlyRea, auditPageRea, auditTotalPagesRea]);

  const sliceAuditFuzzy = useMemo(() => {
    const page = Math.min(auditPageFuzzy, auditTotalPagesFuzzy);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAuditFuzzy.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAuditFuzzy, auditPageFuzzy, auditTotalPagesFuzzy]);

  const sumValorFilteredOrc = useMemo(
    () => filteredAuditOnlyOrc.reduce((s, r) => s + r.sumValorOrcamento, 0),
    [filteredAuditOnlyOrc]
  );
  const sumValorFilteredRea = useMemo(
    () => filteredAuditOnlyRea.reduce((s, r) => s + r.sumValorRealizado, 0),
    [filteredAuditOnlyRea]
  );

  const sumSpellingOrc = useMemo(() => {
    let s = 0;
    for (const d of filteredAuditFuzzy) {
      for (const v of d.orcamentoVariants) {
        s += valorSumByTripletKeyOrc.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0;
      }
    }
    return s;
  }, [filteredAuditFuzzy, valorSumByTripletKeyOrc]);

  const sumSpellingRea = useMemo(() => {
    let s = 0;
    for (const d of filteredAuditFuzzy) {
      for (const v of d.realizadoVariants) {
        s += valorSumByTripletKeyRea.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0;
      }
    }
    return s;
  }, [filteredAuditFuzzy, valorSumByTripletKeyRea]);

  const areaAudit = useMemo(() => {
    const orc = orcamentoTripletRows.map((r) => rowToAreaPair(r.area, r.sub_area));
    const rea = items.map((r) => rowToAreaPair(r.area_tratado, r.sub_area_tratado));
    return auditAreaPairSets(orc, rea);
  }, [orcamentoTripletRows, items]);

  const valorSumByAreaKeyOrc = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of orcamentoTripletRows) {
      const p = rowToAreaPair(r.area, r.sub_area);
      if (!p.area && !p.subArea) continue;
      const k = areaPairKeyExact(p);
      const add = r.valor != null && !Number.isNaN(Number(r.valor)) ? Number(r.valor) : 0;
      m.set(k, (m.get(k) ?? 0) + add);
    }
    return m;
  }, [orcamentoTripletRows]);

  const valorSumByAreaKeyRea = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of items) {
      const p = rowToAreaPair(r.area_tratado, r.sub_area_tratado);
      if (!p.area && !p.subArea) continue;
      const k = areaPairKeyExact(p);
      const add = r.valor != null && !Number.isNaN(Number(r.valor)) ? Number(r.valor) : 0;
      m.set(k, (m.get(k) ?? 0) + add);
    }
    return m;
  }, [items]);

  const onlyOrcamentoAreaEnriched: AreaPairCountValorOrc[] = useMemo(
    () =>
      areaAudit.onlyOrcamento.map((row) => ({
        ...row,
        sumValorOrcamento:
          valorSumByAreaKeyOrc.get(areaPairKeyExact({ area: row.area, subArea: row.subArea })) ?? 0,
      })),
    [areaAudit.onlyOrcamento, valorSumByAreaKeyOrc]
  );

  const onlyRealizadoAreaEnriched: AreaPairCountValorRea[] = useMemo(
    () =>
      areaAudit.onlyRealizado.map((row) => ({
        ...row,
        sumValorRealizado:
          valorSumByAreaKeyRea.get(areaPairKeyExact({ area: row.area, subArea: row.subArea })) ?? 0,
      })),
    [areaAudit.onlyRealizado, valorSumByAreaKeyRea]
  );

  const filteredAreaAuditOnlyOrc = useMemo(
    () => onlyOrcamentoAreaEnriched.filter((row) => areaAuditRowMatchesQuery(row, areaAuditSearch)),
    [onlyOrcamentoAreaEnriched, areaAuditSearch]
  );
  const filteredAreaAuditOnlyRea = useMemo(
    () => onlyRealizadoAreaEnriched.filter((row) => areaAuditRowMatchesQuery(row, areaAuditSearch)),
    [onlyRealizadoAreaEnriched, areaAuditSearch]
  );
  const filteredAreaAuditFuzzy = useMemo(
    () => areaAudit.spellingDivergences.filter((d) => areaSpellingMatchesQuery(d, areaAuditSearch)),
    [areaAudit.spellingDivergences, areaAuditSearch]
  );

  const areaAuditTotalPagesOrc = Math.max(1, Math.ceil(filteredAreaAuditOnlyOrc.length / AUDIT_PAGE_SIZE));
  const areaAuditTotalPagesRea = Math.max(1, Math.ceil(filteredAreaAuditOnlyRea.length / AUDIT_PAGE_SIZE));
  const areaAuditTotalPagesFuzzy = Math.max(1, Math.ceil(filteredAreaAuditFuzzy.length / AUDIT_PAGE_SIZE));

  useEffect(() => {
    setAreaPageOrc((p) => Math.min(Math.max(1, p), areaAuditTotalPagesOrc));
  }, [areaAuditTotalPagesOrc]);
  useEffect(() => {
    setAreaPageRea((p) => Math.min(Math.max(1, p), areaAuditTotalPagesRea));
  }, [areaAuditTotalPagesRea]);
  useEffect(() => {
    setAreaPageFuzzy((p) => Math.min(Math.max(1, p), areaAuditTotalPagesFuzzy));
  }, [areaAuditTotalPagesFuzzy]);

  const sliceAreaAuditOrc = useMemo(() => {
    const page = Math.min(areaPageOrc, areaAuditTotalPagesOrc);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAreaAuditOnlyOrc.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAreaAuditOnlyOrc, areaPageOrc, areaAuditTotalPagesOrc]);

  const sliceAreaAuditRea = useMemo(() => {
    const page = Math.min(areaPageRea, areaAuditTotalPagesRea);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAreaAuditOnlyRea.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAreaAuditOnlyRea, areaPageRea, areaAuditTotalPagesRea]);

  const sliceAreaAuditFuzzy = useMemo(() => {
    const page = Math.min(areaPageFuzzy, areaAuditTotalPagesFuzzy);
    const start = (page - 1) * AUDIT_PAGE_SIZE;
    return filteredAreaAuditFuzzy.slice(start, start + AUDIT_PAGE_SIZE);
  }, [filteredAreaAuditFuzzy, areaPageFuzzy, areaAuditTotalPagesFuzzy]);

  const sumValorAreaFilteredOrc = useMemo(
    () => filteredAreaAuditOnlyOrc.reduce((s, r) => s + r.sumValorOrcamento, 0),
    [filteredAreaAuditOnlyOrc]
  );
  const sumValorAreaFilteredRea = useMemo(
    () => filteredAreaAuditOnlyRea.reduce((s, r) => s + r.sumValorRealizado, 0),
    [filteredAreaAuditOnlyRea]
  );

  const sumAreaSpellingOrc = useMemo(() => {
    let s = 0;
    for (const d of filteredAreaAuditFuzzy) {
      for (const v of d.orcamentoVariants) {
        s += valorSumByAreaKeyOrc.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0;
      }
    }
    return s;
  }, [filteredAreaAuditFuzzy, valorSumByAreaKeyOrc]);

  const sumAreaSpellingRea = useMemo(() => {
    let s = 0;
    for (const d of filteredAreaAuditFuzzy) {
      for (const v of d.realizadoVariants) {
        s += valorSumByAreaKeyRea.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0;
      }
    }
    return s;
  }, [filteredAreaAuditFuzzy, valorSumByAreaKeyRea]);

  const autoMapHeaders = (headers: string[]): Record<string, RealizadoColumnKey> => {
    const map: Record<string, RealizadoColumnKey> = {};
    headers.forEach((header) => {
      const trimmed = header.trim();
      const normalized = normalizeLabel(trimmed);
      const exact = CANONICAL_HEADER_MAP[normalized];
      if (exact) {
        map[header] = exact;
        return;
      }
      for (const dbCol of DB_COLUMNS) {
        const candidates = [dbCol.label, ...(dbCol.aliases || [])].map(normalizeLabel);
        if (candidates.some((candidate) => normalized === candidate || normalized.includes(candidate) || candidate.includes(normalized))) {
          map[header] = dbCol.key;
          break;
        }
      }
    });
    return map;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Selecione um arquivo Excel (.xlsx ou .xls)");
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      const preferredSheet =
        workbook.SheetNames.find((n) => normalizeLabel(n).includes("base para realizado")) || workbook.SheetNames[0];

      const worksheet = workbook.Sheets[preferredSheet];
      const jsonRaw = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null, raw: true }) as any[][];
      const jsonTyped = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null, raw: false }) as any[][];

      for (let i = 0; i < jsonRaw.length; i++) {
        if (!jsonTyped[i]) continue;
        for (let j = 0; j < jsonRaw[i].length; j++) {
          if (jsonTyped[i][j] instanceof Date) jsonRaw[i][j] = jsonTyped[i][j];
        }
      }

      if (jsonRaw.length < 2) {
        toast.error("A planilha está vazia ou sem dados.");
        return;
      }

      const headers = (jsonRaw[0] || []).map((h: unknown, idx: number) => (h ? String(h).trim() : `Coluna ${idx + 1}`));
      const dataRows = jsonRaw.slice(1);

      importMetaRef.current = { id: crypto.randomUUID(), nomeArquivo: file.name };
      setExcelHeaders(headers);
      setExcelData(dataRows);
      setColumnMapping(autoMapHeaders(headers));
      setMappingDialogOpen(true);
      toast.success(`Aba selecionada: ${preferredSheet}`);
    } catch (error: any) {
      console.error("Erro ao ler arquivo:", error);
      toast.error(`Erro ao ler arquivo: ${error.message || "falha desconhecida"}`);
    } finally {
      event.target.value = "";
    }
  };

  const handleImportWithMapping = async () => {
    const meta = importMetaRef.current;
    if (!meta) {
      toast.error("Sessão inválida. Selecione o arquivo novamente.");
      return;
    }
    if (!Object.values(columnMapping).includes("data")) {
      toast.error("Mapeie ao menos a coluna Data.");
      return;
    }

    setUploading(true);
    setImportProgress(0);
    setImportStatus("Processando planilha...");
    setMappingDialogOpen(false);

    try {
      const records: Record<string, unknown>[] = [];
      const yearsFound = new Set<string>();

      for (let rowIndex = 0; rowIndex < excelData.length; rowIndex++) {
        const row = excelData[rowIndex];
        if (!row || row.every((cell) => cell === null || cell === undefined || String(cell).trim() === "")) continue;

        const record: Record<string, unknown> = {
          importacao_id: meta.id,
          nome_arquivo: meta.nomeArquivo,
        };

        DB_COLUMNS.forEach((dbCol) => {
          const excelHeader = Object.keys(columnMapping).find((h) => columnMapping[h] === dbCol.key);
          const excelIndex = excelHeader ? excelHeaders.indexOf(excelHeader) : -1;
          const value = excelIndex >= 0 ? row[excelIndex] : null;

          if (dbCol.key === "data") record.data = parseDate(value);
          else if (
            dbCol.key === "debito" ||
            dbCol.key === "credito" ||
            dbCol.key === "saldo" ||
            dbCol.key === "valor"
          ) {
            record[dbCol.key] = parseNumber(value);
          } else {
            record[dbCol.key] = value ? String(value).trim() : null;
          }
        });

        const ar = record.area_tratado != null ? String(record.area_tratado).trim() : "";
        let sub = record.sub_area_tratado != null ? String(record.sub_area_tratado).trim() : "";
        const arLo = ar.toLowerCase();
        if (arLo === "operações" && /^operações\s+n2$/i.test(sub.replace(/\s+/g, " ").trim())) {
          sub = "OPERAÇÕES";
          record.sub_area_tratado = sub;
        }
        if (ar && (sub === "-" || sub === "–") && areaUsaSubHomonimaOrcamento(ar)) {
          record.sub_area_tratado = ar;
        }
        const subAfterN2 =
          record.sub_area_tratado != null ? String(record.sub_area_tratado).trim() : "";
        if (
          ar &&
          subAfterN2 &&
          ar.toLowerCase() === subAfterN2.toLowerCase() &&
          !areaUsaSubHomonimaOrcamento(ar)
        ) {
          record.sub_area_tratado = "-";
        }

        if (record.data) yearsFound.add(String(record.data).slice(0, 4));
        records.push(record);

        if (rowIndex % 300 === 0) {
          setImportProgress(Math.min(60, Math.round(((rowIndex + 1) / Math.max(excelData.length, 1)) * 60)));
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      if (records.length === 0) {
        toast.error("Nenhum registro válido encontrado.");
        return;
      }

      const batchSize = 500;
      const maxConcurrentBatches = 3;
      const totalBatches = Math.ceil(records.length / batchSize);

      for (let i = 0; i < totalBatches; i += maxConcurrentBatches) {
        const promises: Promise<void>[] = [];
        for (let j = i; j < Math.min(i + maxConcurrentBatches, totalBatches); j++) {
          const batch = records.slice(j * batchSize, (j + 1) * batchSize);
          promises.push(
            (async () => {
              const { error } = await supabase.from(TABLE).insert(batch);
              if (error) throw new Error(`Lote ${j + 1}/${totalBatches}: ${error.message}`);
            })()
          );
        }
        setImportStatus(`Enviando lotes ${i + 1} a ${Math.min(i + maxConcurrentBatches, totalBatches)} de ${totalBatches}...`);
        await Promise.all(promises);
        const uploaded = Math.min(i + maxConcurrentBatches, totalBatches);
        setImportProgress(60 + Math.round((uploaded / totalBatches) * 40));
      }

      toast.success(`${records.length} linhas importadas com sucesso.`);
      await fetchYears();
      const importedYear = Array.from(yearsFound).sort().reverse()[0];
      if (importedYear) setSelectedYear(importedYear);
      else if (selectedYearRef.current) fetchItems(selectedYearRef.current);
    } catch (error: any) {
      console.error("Erro ao importar:", error);
      toast.error(`Erro ao importar: ${error.message || "falha desconhecida"}`);
    } finally {
      setUploading(false);
      setImportStatus("");
      setImportProgress(0);
    }
  };

  const handleDeleteYear = async () => {
    if (!selectedYear) return;
    if (!confirm(`Excluir todos os registros de ${selectedYear}?`)) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from(TABLE)
        .delete()
        .gte("data", `${selectedYear}-01-01`)
        .lte("data", `${selectedYear}-12-31`);
      if (error) throw error;
      toast.success(`Registros de ${selectedYear} removidos.`);
      await fetchYears();
      setItems([]);
    } catch (error) {
      console.error("Erro ao excluir:", error);
      toast.error("Erro ao excluir registros.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | null | undefined) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const truncateCellKeys: RealizadoColumnKey[] = [
    "conta",
    "nome",
    "memorando",
    "descricao_receita_despesa",
    "classificacao_receita_despesa",
    "tipo_receita_despesa",
    "cadastro_federal_cliente",
    "cadastro_federal_fornecedor",
  ];

  const cellForColumn = (item: RealizadoItem, key: RealizadoColumnKey): { node: React.ReactNode; title?: string } => {
    if (key === "data") {
      return { node: formatDate(item.data), title: item.data || undefined };
    }
    if (key === "debito" || key === "credito" || key === "saldo" || key === "valor") {
      const n = item[key];
      return { node: formatCurrency(n as number | null) };
    }
    const raw = item[key as keyof RealizadoItem];
    const s = raw != null && String(raw).trim() !== "" ? String(raw) : null;
    const display = s ?? "-";
    return { node: display, title: s && s.length > 48 ? s : undefined };
  };

  const totalDebito = useMemo(() => items.reduce((sum, item) => sum + (item.debito || 0), 0), [items]);
  const totalCredito = useMemo(() => items.reduce((sum, item) => sum + (item.credito || 0), 0), [items]);
  const totalValor = useMemo(() => items.reduce((sum, item) => sum + (item.valor || 0), 0), [items]);
  const totalItems = items.length;

  return (
    <div className="space-y-4 p-6 max-w-7xl w-full min-w-0 mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Realizado (financeiro)</h2>
          <p className="text-muted-foreground text-sm max-w-2xl">
            Importação espelhada da planilha oficial (aba &quot;Base para Realizado 2026&quot;), complementar ao realizado do Pipefy.
            Correções da área financeira usam o botão de edição na grade e ficam registradas no histórico de alterações.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="w-[120px]">
            {years.length > 0 && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button variant="outline" onClick={() => selectedYear && fetchItems(selectedYear)} disabled={loading || !selectedYear}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <label htmlFor="realizado-file-upload">
            <Button asChild variant="default" disabled={uploading}>
              <span>
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? `Importando... ${importProgress}%` : "Importar Excel"}
              </span>
            </Button>
          </label>
          <input
            id="realizado-file-upload"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          {items.length > 0 && selectedYear && (
            <Button variant="destructive" onClick={handleDeleteYear} disabled={loading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar {selectedYear}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mapear colunas da planilha</DialogTitle>
            <DialogDescription>
              Mesmas 21 colunas da aba oficial (A–U), incluindo AREA tratado, SUB AREA tratado, VALOR e Tipo/Classificação/Descrição de receita-despesa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {DB_COLUMNS.map((dbCol) => (
              <div key={dbCol.key} className="flex items-center gap-4">
                <div className="w-64 flex-shrink-0">
                  <Label className="flex items-center gap-2">
                    {dbCol.label}
                    {dbCol.required && <span className="text-red-500 text-xs">*</span>}
                  </Label>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1">
                  <Select
                    value={Object.keys(columnMapping).find((h) => columnMapping[h] === dbCol.key) || "__unmapped__"}
                    onValueChange={(value) => {
                      const next = { ...columnMapping };
                      const old = Object.keys(next).find((k) => next[k] === dbCol.key);
                      if (old) delete next[old];
                      if (value && value !== "__unmapped__") {
                        if (next[value]) delete next[value];
                        next[value] = dbCol.key;
                      }
                      setColumnMapping(next);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Coluna da planilha..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unmapped__">-- Não mapear --</SelectItem>
                      {excelHeaders.map((header, idx) => (
                        <SelectItem key={idx} value={header}>
                          {header} (Coluna {idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleImportWithMapping} disabled={!Object.values(columnMapping).includes("data")}>
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Registros ({selectedYear || "—"})</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItems}</div>
            <p className="text-xs text-muted-foreground">Linhas no ano (carga completa)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Débito</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalDebito)}</div>
            <p className="text-xs text-muted-foreground">Soma de débito no ano</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Crédito</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalCredito)}</div>
            <p className="text-xs text-muted-foreground">Soma de crédito no ano</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Valor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValor)}</div>
            <p className="text-xs text-muted-foreground">Soma da coluna VALOR no ano</p>
          </CardContent>
        </Card>
      </div>

      {uploading && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{importStatus || "Importando..."}</span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${importProgress}%` }} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Registros importados</CardTitle>
          <CardDescription>
            Visualização da tabela {TABLE}. Arraste a barra de rolagem horizontal abaixo da grade para ver todas as colunas.
          </CardDescription>
        </CardHeader>
        <CardContent className="min-w-0 space-y-4">
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {years.length > 0 ? "Nenhum registro encontrado para este ano." : "Nenhum registro encontrado."}
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <div className="relative w-full min-w-0 sm:max-w-md sm:flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Buscar (conta, nome, doc., memorando, áreas, valores…)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    aria-label="Buscar registros"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span className="whitespace-nowrap">Linhas por página</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-9 w-[88px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100, 200].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="w-full min-w-0 rounded-md border">
                <Table className="w-max min-w-full">
                  <TableHeader>
                    <TableRow>
                      {DB_COLUMNS.map((col) => (
                        <TableHead key={col.key} className="whitespace-nowrap">
                          {col.label}
                        </TableHead>
                      ))}
                      <TableHead className="sticky right-0 bg-background whitespace-nowrap shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                        Ações
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedItems.map((item) => (
                      <TableRow key={item.id}>
                        {DB_COLUMNS.map((col) => {
                          const { node, title } = cellForColumn(item, col.key);
                          const trunc = truncateCellKeys.includes(col.key);
                          return (
                            <TableCell
                              key={col.key}
                              className={trunc ? "max-w-[160px] truncate" : undefined}
                              title={title}
                            >
                              {node}
                            </TableCell>
                          );
                        })}
                        <TableCell className="sticky right-0 bg-background whitespace-nowrap shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                          <div className="flex items-center gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Corrigir linha"
                              onClick={() => {
                                setItemParaCorrigir(item);
                                setCorrigirOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Histórico desta linha"
                              onClick={() => setHistoricoRegistroId(item.id)}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {filteredItems.length === 0 ? (
                    <>
                      Nenhuma linha corresponde à busca ({totalItems} no ano).
                    </>
                  ) : (
                    <>
                      Exibindo{" "}
                      <span className="font-medium text-foreground">
                        {(tablePage - 1) * pageSize + 1}–{Math.min(tablePage * pageSize, filteredItems.length)}
                      </span>{" "}
                      de <span className="font-medium text-foreground">{filteredItems.length}</span>
                      {searchQuery.trim() ? (
                        <>
                          {" "}
                          (filtrado de {totalItems} no ano)
                        </>
                      ) : null}
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    disabled={tablePage <= 1}
                    onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="min-w-[5.5rem] text-center text-sm tabular-nums text-muted-foreground">
                    {tablePage} / {totalTablePages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    disabled={tablePage >= totalTablePages}
                    onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <RealizadoCorrigirLinhaDialog
        open={corrigirOpen}
        onOpenChange={setCorrigirOpen}
        item={itemParaCorrigir}
        onSuccess={() => {
          if (selectedYear) fetchItems(selectedYear);
          setHistoricoRefresh((n) => n + 1);
        }}
      />

      {selectedYear ? (
        <>
        <RealizadoHistoricoAlteracoes
          selectedYear={selectedYear}
          registroIdFilter={historicoRegistroId}
          onClearRegistroFilter={() => setHistoricoRegistroId(null)}
          refreshToken={historicoRefresh}
        />

        <Card className="min-w-0">
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitCompare className="h-5 w-5 shrink-0 text-muted-foreground" />
                  Auditoria: tríade orçamento × realizado
                </CardTitle>
                <CardDescription>
                  Compara as três colunas de classificação orçamentária (
                  <strong>Tipo</strong>, <strong>Classificação</strong>, <strong>Descrição</strong> R/D) entre{" "}
                  <code className="text-xs">solis_orcamento</code> (linhas com <code className="text-xs">mes</code> no ano) e{" "}
                  <code className="text-xs">{TABLE}</code> (datas no ano). Igualdade exata após <code className="text-xs">trim</code> nos
                  três campos.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4">
            {loading || loadingOrcamentoAudit ? (
              <div className="text-sm text-muted-foreground py-4">Carregando dados para auditoria…</div>
            ) : (
              <>
                <Alert variant="default" className="bg-muted/40">
                  <AlertTitle className="text-sm">Metodologia e limites</AlertTitle>
                  <AlertDescription className="text-xs space-y-1">
                    <span>
                      O orçamento é filtrado como na tela de importação de orçamento: <code>mes</code> começando com{" "}
                      <code>{selectedYear}-</code>. Linhas só em <code>data</code> sem <code>mes</code> no padrão podem ficar de fora.
                    </span>
                    <span className="block pt-1">
                      A aba <strong>Grafia normalizada</strong> lista casos em que a tríade coincide após minúsculas, remoção de acentos e
                      colapso de espaços, mas o texto bruto difere entre planilhas — candidatos a padronizar nomes.
                    </span>
                  </AlertDescription>
                </Alert>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Linhas orçamento (filtro ano)</p>
                    <p className="text-xl font-semibold tabular-nums">{tripletAudit.orcamentoRowsAnalisadas}</p>
                    <p className="text-xs text-muted-foreground mt-1">Sem tríade preenchida: {tripletAudit.orcamentoSemTriade}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Linhas realizado (ano)</p>
                    <p className="text-xl font-semibold tabular-nums">{tripletAudit.realizadoRowsAnalisadas}</p>
                    <p className="text-xs text-muted-foreground mt-1">Sem tríade preenchida: {tripletAudit.realizadoSemTriade}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Tríades distintas (exato)</p>
                    <p className="text-xl font-semibold tabular-nums">
                      {tripletAudit.orcamentoDistinct} / {tripletAudit.realizadoDistinct}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Orçamento · Realizado</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Em comum (exato)</p>
                    <p className="text-xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">{tripletAudit.bothExact}</p>
                    <p className="text-xs text-muted-foreground mt-1">Combinações idênticas nos dois lados</p>
                  </div>
                </div>

                <div className="relative max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Filtrar tríades nestas abas…"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    className="pl-9"
                    aria-label="Filtrar auditoria"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Só orçamento: {tripletAudit.onlyOrcamento.length}</Badge>
                  <Badge variant="secondary">Só realizado: {tripletAudit.onlyRealizado.length}</Badge>
                  <Badge variant="outline">Grafia / norm.: {tripletAudit.spellingDivergences.length}</Badge>
                </div>

                <Tabs value={auditActiveTab} onValueChange={setAuditActiveTab} className="w-full min-w-0">
                  <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                    <TabsTrigger value="only-orc" className="text-xs sm:text-sm">
                      Só orçamento ({tripletAudit.onlyOrcamento.length})
                    </TabsTrigger>
                    <TabsTrigger value="only-rea" className="text-xs sm:text-sm">
                      Só realizado ({tripletAudit.onlyRealizado.length})
                    </TabsTrigger>
                    <TabsTrigger value="fuzzy" className="text-xs sm:text-sm">
                      Grafia normalizada ({tripletAudit.spellingDivergences.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="only-orc" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Combinações que aparecem no orçamento e nenhuma linha do realizado tem a mesma tríade (após trim). A coluna Σ orçamento
                      soma o campo <code className="text-[10px]">valor</code> das linhas de orçamento com essa tríade.
                    </p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo R/D</TableHead>
                            <TableHead>Classificação</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Linhas</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ orçamento</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAuditOnlyOrc.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-muted-foreground">
                                {tripletAudit.onlyOrcamento.length === 0
                                  ? "Nenhuma — todas as tríades do orçamento aparecem no realizado (exato)."
                                  : "Nenhuma linha corresponde ao filtro de busca."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAuditOrc.map((row, idx) => (
                              <TableRow key={`o-${idx}-${row.tipo}-${row.count}-${row.sumValorOrcamento}`}>
                                <TableCell className="max-w-[200px] align-top">{row.tipo || "—"}</TableCell>
                                <TableCell className="max-w-[200px] align-top">{row.classif || "—"}</TableCell>
                                <TableCell className="max-w-[220px] align-top">{row.descr || "—"}</TableCell>
                                <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                                <TableCell className="text-right tabular-nums whitespace-nowrap">{formatCurrency(row.sumValorOrcamento)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAuditOnlyOrc.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Total filtrado: <span className="font-medium text-foreground">{filteredAuditOnlyOrc.length}</span> tríades ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumValorFilteredOrc)}</span> em valor orçado
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageOrc <= 1}
                            onClick={() => setAuditPageOrc((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(auditPageOrc, auditTotalPagesOrc)} / {auditTotalPagesOrc}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageOrc >= auditTotalPagesOrc}
                            onClick={() => setAuditPageOrc((p) => Math.min(auditTotalPagesOrc, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>

                  <TabsContent value="only-rea" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Combinações no realizado financeiro sem correspondência exata no orçamento. Σ realizado soma a coluna{" "}
                      <code className="text-[10px]">valor</code> do complementar.
                    </p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo R/D</TableHead>
                            <TableHead>Classificação</TableHead>
                            <TableHead>Descrição</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Linhas</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ realizado</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAuditOnlyRea.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-muted-foreground">
                                {tripletAudit.onlyRealizado.length === 0
                                  ? "Nenhuma — toda tríade do realizado existe no orçamento (exato)."
                                  : "Nenhuma linha corresponde ao filtro de busca."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAuditRea.map((row, idx) => (
                              <TableRow key={`r-${idx}-${row.tipo}-${row.count}-${row.sumValorRealizado}`}>
                                <TableCell className="max-w-[200px] align-top">{row.tipo || "—"}</TableCell>
                                <TableCell className="max-w-[200px] align-top">{row.classif || "—"}</TableCell>
                                <TableCell className="max-w-[220px] align-top">{row.descr || "—"}</TableCell>
                                <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                                <TableCell className="text-right tabular-nums whitespace-nowrap">{formatCurrency(row.sumValorRealizado)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAuditOnlyRea.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Total filtrado: <span className="font-medium text-foreground">{filteredAuditOnlyRea.length}</span> tríades ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumValorFilteredRea)}</span> em valor realizado
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageRea <= 1}
                            onClick={() => setAuditPageRea((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(auditPageRea, auditTotalPagesRea)} / {auditTotalPagesRea}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageRea >= auditTotalPagesRea}
                            onClick={() => setAuditPageRea((p) => Math.min(auditTotalPagesRea, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>

                  <TabsContent value="fuzzy" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Mesma tríade após normalização leve, mas nenhuma igualdade exata entre os dois lados. Totais por grupo usam somas de{" "}
                      <code className="text-[10px]">valor</code> por variante textual.
                    </p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="whitespace-nowrap">Chave normalizada</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ orç.</TableHead>
                            <TableHead>Variantes (orçamento)</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ realiz.</TableHead>
                            <TableHead>Variantes (realizado)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAuditFuzzy.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-muted-foreground">
                                {tripletAudit.spellingDivergences.length === 0
                                  ? "Nenhuma divergência desse tipo encontrada."
                                  : "Nenhum grupo corresponde ao filtro de busca."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAuditFuzzy.map((d, idx) => {
                              const sumOrcG = d.orcamentoVariants.reduce(
                                (s, v) => s + (valorSumByTripletKeyOrc.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0),
                                0
                              );
                              const sumReaG = d.realizadoVariants.reduce(
                                (s, v) => s + (valorSumByTripletKeyRea.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0),
                                0
                              );
                              const fmt = (v: { tipo: string; classif: string; descr: string; count: number }, side: "orc" | "rea") => {
                                const sum =
                                  side === "orc"
                                    ? valorSumByTripletKeyOrc.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0
                                    : valorSumByTripletKeyRea.get(keyExact({ tipo: v.tipo, classif: v.classif, descr: v.descr })) ?? 0;
                                return `${v.tipo || "—"} / ${v.classif || "—"} / ${v.descr || "—"} · ${v.count} linhas · ${formatCurrency(sum)}`;
                              };
                              return (
                                <TableRow key={`f-${idx}-${d.normalizedKey}`}>
                                  <TableCell className="max-w-[160px] align-top font-mono text-xs break-all">{d.normalizedKey}</TableCell>
                                  <TableCell className="align-top text-right tabular-nums whitespace-nowrap text-xs">
                                    {formatCurrency(sumOrcG)}
                                  </TableCell>
                                  <TableCell className="max-w-[260px] align-top text-xs whitespace-pre-wrap">
                                    {d.orcamentoVariants.map((v, i) => (
                                      <span key={i} className="block border-b border-dotted border-border/60 py-1 last:border-0">
                                        {fmt(v, "orc")}
                                      </span>
                                    ))}
                                  </TableCell>
                                  <TableCell className="align-top text-right tabular-nums whitespace-nowrap text-xs">
                                    {formatCurrency(sumReaG)}
                                  </TableCell>
                                  <TableCell className="max-w-[260px] align-top text-xs whitespace-pre-wrap">
                                    {d.realizadoVariants.map((v, i) => (
                                      <span key={i} className="block border-b border-dotted border-border/60 py-1 last:border-0">
                                        {fmt(v, "rea")}
                                      </span>
                                    ))}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAuditFuzzy.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Totais no filtro: <span className="font-medium text-foreground">{formatCurrency(sumSpellingOrc)}</span> orç. ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumSpellingRea)}</span> realiz. ·{" "}
                          {filteredAuditFuzzy.length} grupo(s)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageFuzzy <= 1}
                            onClick={() => setAuditPageFuzzy((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(auditPageFuzzy, auditTotalPagesFuzzy)} / {auditTotalPagesFuzzy}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={auditPageFuzzy >= auditTotalPagesFuzzy}
                            onClick={() => setAuditPageFuzzy((p) => Math.min(auditTotalPagesFuzzy, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>
                </Tabs>

                <p className="text-xs text-muted-foreground border-t pt-3 leading-relaxed">
                  <strong>Próximo passo (view principal):</strong> para somar o realizado complementar na view de orçamento, evite o nome{" "}
                  <code>Historico</code> — na planilha de orçamento já existe a coluna <strong>Histórico</strong> (conteúdo diferente). Sugestões
                  mais claras: <code>valor_realizado_financeiro_complementar</code>, <code>soma_valor_realizado_complementar</code> ou{" "}
                  <code>realizado_financeiro_ytd</code> (se for acumulado no ano).
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPinned className="h-5 w-5 shrink-0 text-muted-foreground" />
                  Auditoria: área × subárea
                </CardTitle>
                <CardDescription>
                  Orçamento: colunas <code className="text-xs">area</code> e <code className="text-xs">sub_area</code> em{" "}
                  <code className="text-xs">solis_orcamento</code>. Realizado: <strong>AREA tratado</strong> e <strong>SUB AREA tratado</strong>{" "}
                  em <code className="text-xs">{TABLE}</code>. Comparação exata após <code className="text-xs">trim</code> em cada campo.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="min-w-0 space-y-4">
            {loading || loadingOrcamentoAudit ? (
              <div className="text-sm text-muted-foreground py-4">Carregando dados para auditoria…</div>
            ) : (
              <>
                <Alert variant="default" className="bg-muted/40">
                  <AlertTitle className="text-sm">Metodologia</AlertTitle>
                  <AlertDescription className="text-xs space-y-1">
                    <span>
                      Mesmo filtro de ano do bloco anterior (orçamento por <code>mes</code>, realizado por <code>data</code>). Linhas sem área
                      nem subárea preenchidas contam como &quot;sem par&quot; e não entram nas combinações distintas.
                    </span>
                    <span className="block pt-1">
                      <strong>Grafia normalizada</strong>: mesma lógica da tríade — texto igual após minúsculas, sem acento e espaços
                      colapsados, sem par exato cruzado entre as fontes.
                    </span>
                  </AlertDescription>
                </Alert>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Linhas orçamento</p>
                    <p className="text-xl font-semibold tabular-nums">{areaAudit.orcamentoRowsAnalisadas}</p>
                    <p className="text-xs text-muted-foreground mt-1">Sem área/subárea: {areaAudit.orcamentoSemPar}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Linhas realizado</p>
                    <p className="text-xl font-semibold tabular-nums">{areaAudit.realizadoRowsAnalisadas}</p>
                    <p className="text-xs text-muted-foreground mt-1">Sem área/subárea: {areaAudit.realizadoSemPar}</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Pares distintos (exato)</p>
                    <p className="text-xl font-semibold tabular-nums">
                      {areaAudit.orcamentoDistinct} / {areaAudit.realizadoDistinct}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Orçamento · Realizado</p>
                  </div>
                  <div className="rounded-lg border bg-card p-3 text-sm">
                    <p className="text-muted-foreground text-xs">Em comum (exato)</p>
                    <p className="text-xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">{areaAudit.bothExact}</p>
                    <p className="text-xs text-muted-foreground mt-1">Mesmo par área+subárea</p>
                  </div>
                </div>

                <div className="relative max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Filtrar pares área/subárea…"
                    value={areaAuditSearch}
                    onChange={(e) => setAreaAuditSearch(e.target.value)}
                    className="pl-9"
                    aria-label="Filtrar auditoria por área"
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Só orçamento: {areaAudit.onlyOrcamento.length}</Badge>
                  <Badge variant="secondary">Só realizado: {areaAudit.onlyRealizado.length}</Badge>
                  <Badge variant="outline">Grafia / norm.: {areaAudit.spellingDivergences.length}</Badge>
                </div>

                <Tabs value={areaAuditTab} onValueChange={setAreaAuditTab} className="w-full min-w-0">
                  <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
                    <TabsTrigger value="a-orc" className="text-xs sm:text-sm">
                      Só orçamento ({areaAudit.onlyOrcamento.length})
                    </TabsTrigger>
                    <TabsTrigger value="a-rea" className="text-xs sm:text-sm">
                      Só realizado ({areaAudit.onlyRealizado.length})
                    </TabsTrigger>
                    <TabsTrigger value="a-fuzzy" className="text-xs sm:text-sm">
                      Grafia normalizada ({areaAudit.spellingDivergences.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="a-orc" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Pares <code className="text-[10px]">area</code> + <code className="text-[10px]">sub_area</code> no orçamento sem
                      correspondência exata em <code className="text-[10px]">AREA tratado</code> + <code className="text-[10px]">SUB AREA tratado</code> no realizado.
                    </p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Área principal</TableHead>
                            <TableHead>Subárea</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Linhas</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ orçamento</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAreaAuditOnlyOrc.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-muted-foreground">
                                {areaAudit.onlyOrcamento.length === 0
                                  ? "Nenhum par só no orçamento."
                                  : "Nenhum par corresponde ao filtro de busca."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAreaAuditOrc.map((row, idx) => (
                              <TableRow key={`ao-${idx}-${row.area}-${row.count}`}>
                                <TableCell className="max-w-[260px] align-top">{row.area || "—"}</TableCell>
                                <TableCell className="max-w-[220px] align-top">{row.subArea || "—"}</TableCell>
                                <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                                <TableCell className="text-right tabular-nums whitespace-nowrap">{formatCurrency(row.sumValorOrcamento)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAreaAuditOnlyOrc.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Total filtrado: <span className="font-medium text-foreground">{filteredAreaAuditOnlyOrc.length}</span> pares ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumValorAreaFilteredOrc)}</span>
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageOrc <= 1}
                            onClick={() => setAreaPageOrc((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(areaPageOrc, areaAuditTotalPagesOrc)} / {areaAuditTotalPagesOrc}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageOrc >= areaAuditTotalPagesOrc}
                            onClick={() => setAreaPageOrc((p) => Math.min(areaAuditTotalPagesOrc, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>

                  <TabsContent value="a-rea" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Pares no realizado complementar sem par exato no orçamento (colunas <code className="text-[10px]">area</code> /{" "}
                      <code className="text-[10px]">sub_area</code>).
                    </p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead>AREA tratado</TableHead>
                            <TableHead>SUB AREA tratado</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Linhas</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ realizado</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAreaAuditOnlyRea.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-muted-foreground">
                                {areaAudit.onlyRealizado.length === 0
                                  ? "Nenhum par só no realizado."
                                  : "Nenhum par corresponde ao filtro de busca."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAreaAuditRea.map((row, idx) => (
                              <TableRow key={`ar-${idx}-${row.area}-${row.count}`}>
                                <TableCell className="max-w-[260px] align-top">{row.area || "—"}</TableCell>
                                <TableCell className="max-w-[220px] align-top">{row.subArea || "—"}</TableCell>
                                <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                                <TableCell className="text-right tabular-nums whitespace-nowrap">{formatCurrency(row.sumValorRealizado)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAreaAuditOnlyRea.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Total filtrado: <span className="font-medium text-foreground">{filteredAreaAuditOnlyRea.length}</span> pares ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumValorAreaFilteredRea)}</span>
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageRea <= 1}
                            onClick={() => setAreaPageRea((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(areaPageRea, areaAuditTotalPagesRea)} / {areaAuditTotalPagesRea}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageRea >= areaAuditTotalPagesRea}
                            onClick={() => setAreaPageRea((p) => Math.min(areaAuditTotalPagesRea, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>

                  <TabsContent value="a-fuzzy" className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">Mesmo par normalizado nos dois lados, texto exato diferente e sem cruzamento exato.</p>
                    <div className="w-full min-w-0 max-h-[min(70vh,28rem)] overflow-auto rounded-md border [scrollbar-width:thin]">
                      <Table className="w-max min-w-full text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="whitespace-nowrap">Chave normalizada</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ orç.</TableHead>
                            <TableHead>Variantes (orçamento)</TableHead>
                            <TableHead className="text-right whitespace-nowrap">Σ realiz.</TableHead>
                            <TableHead>Variantes (realizado)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAreaAuditFuzzy.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-muted-foreground">
                                {areaAudit.spellingDivergences.length === 0
                                  ? "Nenhuma divergência desse tipo."
                                  : "Nenhum grupo corresponde ao filtro."}
                              </TableCell>
                            </TableRow>
                          ) : (
                            sliceAreaAuditFuzzy.map((d, idx) => {
                              const sumOrcG = d.orcamentoVariants.reduce(
                                (s, v) => s + (valorSumByAreaKeyOrc.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0),
                                0
                              );
                              const sumReaG = d.realizadoVariants.reduce(
                                (s, v) => s + (valorSumByAreaKeyRea.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0),
                                0
                              );
                              const fmtA = (v: AreaPairCount, side: "orc" | "rea") => {
                                const sum =
                                  side === "orc"
                                    ? valorSumByAreaKeyOrc.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0
                                    : valorSumByAreaKeyRea.get(areaPairKeyExact({ area: v.area, subArea: v.subArea })) ?? 0;
                                return `${v.area || "—"} / ${v.subArea || "—"} · ${v.count} linhas · ${formatCurrency(sum)}`;
                              };
                              return (
                                <TableRow key={`af-${idx}-${d.normalizedKey}`}>
                                  <TableCell className="max-w-[160px] align-top font-mono text-xs break-all">{d.normalizedKey}</TableCell>
                                  <TableCell className="align-top text-right tabular-nums whitespace-nowrap text-xs">
                                    {formatCurrency(sumOrcG)}
                                  </TableCell>
                                  <TableCell className="max-w-[260px] align-top text-xs whitespace-pre-wrap">
                                    {d.orcamentoVariants.map((v, i) => (
                                      <span key={i} className="block border-b border-dotted border-border/60 py-1 last:border-0">
                                        {fmtA(v, "orc")}
                                      </span>
                                    ))}
                                  </TableCell>
                                  <TableCell className="align-top text-right tabular-nums whitespace-nowrap text-xs">
                                    {formatCurrency(sumReaG)}
                                  </TableCell>
                                  <TableCell className="max-w-[260px] align-top text-xs whitespace-pre-wrap">
                                    {d.realizadoVariants.map((v, i) => (
                                      <span key={i} className="block border-b border-dotted border-border/60 py-1 last:border-0">
                                        {fmtA(v, "rea")}
                                      </span>
                                    ))}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    {filteredAreaAuditFuzzy.length > 0 ? (
                      <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted-foreground">
                          Totais no filtro: <span className="font-medium text-foreground">{formatCurrency(sumAreaSpellingOrc)}</span> orç. ·{" "}
                          <span className="font-medium text-foreground">{formatCurrency(sumAreaSpellingRea)}</span> realiz. ·{" "}
                          {filteredAreaAuditFuzzy.length} grupo(s)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageFuzzy <= 1}
                            onClick={() => setAreaPageFuzzy((p) => Math.max(1, p - 1))}
                            aria-label="Página anterior"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[5rem] text-center text-xs tabular-nums text-muted-foreground">
                            {Math.min(areaPageFuzzy, areaAuditTotalPagesFuzzy)} / {areaAuditTotalPagesFuzzy}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={areaPageFuzzy >= areaAuditTotalPagesFuzzy}
                            onClick={() => setAreaPageFuzzy((p) => Math.min(areaAuditTotalPagesFuzzy, p + 1))}
                            aria-label="Próxima página"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </TabsContent>
                </Tabs>
              </>
            )}
          </CardContent>
        </Card>
        </>
      ) : null}
    </div>
  );
}
